import React, { useEffect, useState } from "react";
import cn from "classnames";
import { getCampaignRecipients } from "../../../api/campaigns.service";
import styles from "./Recipients.module.sass";

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

/**
 * Estados de entrega que se pueden LISTAR usuario a usuario (los que tienen bucket).
 * "Sin encolar" no está aquí: esos destinatarios aún no existen en ningún bucket, así
 * que se resumen como un contador, no como una lista.
 */
const STATES = [
  { value: "sent", label: "Enviados" },
  { value: "pending", label: "En cola" },
  { value: "failed", label: "Fallidos" },
  { value: "skipped", label: "Omitidos" },
];

const PAGE = 50;

const userLine = (u) => u.email || u.username || u.name || u._id;

/**
 * Tabla de DESTINATARIOS del envío: por estado (enviado / en cola / fallido /
 * omitido), con quién es cada uno (email, nivel, idioma) y no solo su id. Responde a
 * "¿a quién le ha llegado ya y a quién no?" sin entrar a mongosh. Se apoya en el
 * endpoint `/recipients`, que pagina e hidrata los ids.
 */
const Recipients = ({ campaignId, counts, notQueued }) => {
  const [state, setState] = useState("sent");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async (st, skip) => {
    setLoading(true);
    try {
      const resp = await getCampaignRecipients(campaignId, { state: st, skip, limit: PAGE });
      const users = resp?.data?.users ?? [];
      setTotal(resp?.data?.total ?? 0);
      setRows((prev) => (skip > 0 ? [...prev, ...users] : users));
    } catch (e) {
      if (skip === 0) setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(state, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, campaignId]);

  return (
    <div className={styles.recipients}>
      <div className={styles.head}>
        <div className={styles.title}>Destinatarios</div>
        <div className={styles.tabs}>
          {STATES.map((s) => (
            <button
              key={s.value}
              type="button"
              className={cn(styles.tab, { [styles.tabActive]: state === s.value })}
              onClick={() => setState(s.value)}
            >
              {s.label}
              <span className={styles.tabCount}>{fmtNum(counts?.[s.value] ?? 0)}</span>
            </button>
          ))}
          {notQueued > 0 && (
            <span className={styles.tabGhost} title="Aún no entran en ninguna tanda">
              Sin encolar
              <span className={styles.tabCount}>{fmtNum(notQueued)}</span>
            </span>
          )}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className={styles.note}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div className={styles.note}>
          {notQueued > 0 && state === "pending"
            ? `Nadie en cola ahora mismo. Quedan ${fmtNum(notQueued)} por soltar en próximas tandas.`
            : "No hay destinatarios en este estado."}
        </div>
      ) : (
        <>
          <div className={styles.rows}>
            {rows.map((u) => (
              <div key={u._id} className={styles.row}>
                <span className={styles.who}>{userLine(u)}</span>
                <span className={styles.meta}>
                  {u.premium && <span className={styles.tagPlus}>Plus+</span>}
                  {u.level && <span className={styles.tag}>{u.level}</span>}
                  {u.lang && <span className={styles.tag}>{String(u.lang).toUpperCase()}</span>}
                </span>
              </div>
            ))}
          </div>
          {rows.length < total && (
            <button
              type="button"
              className={cn("button-stroke", "button-small", styles.more)}
              disabled={loading}
              onClick={() => load(state, rows.length)}
            >
              {loading ? "Cargando…" : `Cargar más (${fmtNum(total - rows.length)} restantes)`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Recipients;
